/**
 * ConfirmDialog 컴포넌트
 * window.confirm() 대체용 커스텀 확인 다이얼로그
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useConfirmDialog } from "@/contexts/ConfirmContext";

export function ConfirmDialog() {
  const { confirmState, handleConfirm, handleCancel } = useConfirmDialog();
  const [isVisible, setIsVisible] = useState(false);

  // 등장/퇴장 애니메이션
  useEffect(() => {
    if (confirmState.isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [confirmState.isOpen]);

  // 키보드 접근성: ESC로 닫기, Enter로 확인
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      } else if (e.key === "Enter") {
        handleConfirm();
      }
    };

    if (confirmState.isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [confirmState.isOpen, handleCancel, handleConfirm]);

  // 뒤로가기 시 다이얼로그 닫기 (closeTopModal 커스텀 이벤트)
  useEffect(() => {
    if (!confirmState.isOpen) return;
    const handler = (e: Event) => {
      e.preventDefault();
      handleCancel();
    };
    document.addEventListener("closeTopModal", handler);
    return () => document.removeEventListener("closeTopModal", handler);
  }, [confirmState.isOpen, handleCancel]);

  if (!confirmState.isOpen) return null;

  const isDanger = confirmState.variant === "danger";

  // createPortal로 document.body에 렌더링하여
  // 부모의 transform이 fixed 포지셔닝에 영향을 주지 않도록 함
  return createPortal(
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-all duration-200 ${
        isVisible ? "bg-black/50" : "bg-black/0"
      }`}
      onClick={handleCancel}
    >
      <div
        className={`w-full max-w-sm rounded-2xl bg-white shadow-xl transition-all duration-200 ${
          isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="px-6 pt-6 pb-2">
          <h3 className="text-lg font-semibold text-slate-900">{confirmState.title}</h3>
          {confirmState.message && (
            <p className="mt-2 text-sm text-slate-600">{confirmState.message}</p>
          )}
        </div>

        {/* 버튼 영역 */}
        <div className="flex gap-3 px-6 pb-6 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 active:bg-slate-100"
          >
            {confirmState.cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium text-white transition-colors ${
              isDanger
                ? "bg-red-500 hover:bg-red-600 active:bg-red-700"
                : "bg-slate-800 hover:bg-slate-900 active:bg-slate-950"
            }`}
          >
            {confirmState.confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
